import React, { useState } from 'react';
import { CheckCircle, FileText, AlertCircle } from 'lucide-react';
import { Button, Card, FileUploadZone, Alert, Badge, ToolActionButtons } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const ValidatePDF = () => {
    const [file, setFile] = useState(null);
    const [validation, setValidation] = useState(null);

    const handleFileSelected = (files) => {
        if (files && files.length > 0) {
            setFile(files[0]);
            setValidation(null);
            toast.success('PDF loaded');
        }
    };

    const validatePDF = async () => {
        if (!file) return;

        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await PDFDocument.load(arrayBuffer);

            const results = {
                isValid: true,
                checks: [
                    { name: 'File Structure', passed: true, message: 'PDF structure is valid' },
                    { name: 'Page Count', passed: pdf.getPageCount() > 0, message: `${pdf.getPageCount()} pages found` },
                    { name: 'Metadata', passed: true, message: 'Metadata is accessible' },
                    { name: 'File Size', passed: file.size < 100 * 1024 * 1024, message: `${(file.size / 1024 / 1024).toFixed(2)} MB` }
                ],
                info: {
                    pages: pdf.getPageCount(),
                    title: pdf.getTitle() || 'N/A',
                    author: pdf.getAuthor() || 'N/A',
                    fileSize: `${(file.size / 1024).toFixed(2)} KB`
                }
            };

            results.isValid = results.checks.every(check => check.passed);

            setValidation(results);
            toast.success('Validation complete!');

        } catch (error) {
            setValidation({
                isValid: false,
                checks: [
                    { name: 'File Structure', passed: false, message: error.message }
                ],
                info: null
            });
            toast.error('PDF validation failed');
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={validation ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <CheckCircle size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Validate PDF
                </h1>
                <p className="tool-description">
                    Check PDF integrity, structure, and compliance.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <>
                    <Alert variant="success">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{file.name}</span>
                            <Button variant="ghost" size="sm" onClick={() => { setFile(null); setValidation(null); }}>
                                Change PDF
                            </Button>
                        </div>
                    </Alert>

                    {!validation && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <Button variant="primary" size="lg" onClick={validatePDF} fullWidth>
                                <CheckCircle size={20} />
                                Validate PDF
                            </Button>
                        </div>
                    )}

                    {validation && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <Card>
                                <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                    Validation Results
                                </h3>

                                <Alert variant={validation.isValid ? 'success' : 'error'} style={{ marginBottom: 'var(--spacing-4)' }}>
                                    {validation.isValid ? (
                                        <><CheckCircle size={20} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} /> <strong>PDF is valid</strong></>
                                    ) : (
                                        <><AlertCircle size={20} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} /> <strong>PDF has issues</strong></>
                                    )}
                                </Alert>

                                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                                    <h4 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-3)' }}>
                                        Validation Checks
                                    </h4>
                                    {validation.checks.map((check, i) => (
                                        <div key={i} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--spacing-2)',
                                            padding: 'var(--spacing-3)',
                                            marginBottom: 'var(--spacing-2)',
                                            backgroundColor: 'var(--color-surface-elevated)',
                                            borderRadius: 'var(--radius-md)',
                                            borderLeft: `4px solid ${check.passed ? 'var(--color-success)' : 'var(--color-error)'}`
                                        }}>
                                            {check.passed ? <CheckCircle size={16} color="var(--color-success)" /> : <AlertCircle size={16} color="var(--color-error)" />}
                                            <div>
                                                <div style={{ fontWeight: 'var(--font-semibold)' }}>{check.name}</div>
                                                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{check.message}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {validation.info && (
                                    <div>
                                        <h4 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-3)' }}>
                                            PDF Information
                                        </h4>
                                        <div style={{ fontSize: 'var(--text-sm)', lineHeight: 1.8 }}>
                                            <div>Pages: {validation.info.pages}</div>
                                            <div>Title: {validation.info.title}</div>
                                            <div>Author: {validation.info.author}</div>
                                            <div>File Size: {validation.info.fileSize}</div>
                                        </div>
                                    </div>
                                )}
                            </Card>

                            <div style={{ marginTop: 'var(--spacing-4)' }}>
                                <Button variant="secondary" size="sm" onClick={() => setValidation(null)}>
                                    Validate Again
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ValidatePDF;
